import { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Grid,
    FormControl,
    FormLabel,
    RadioGroup,
    FormControlLabel,
    Radio,
    Typography,
} from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
    asyncCreateAnswer,
    asyncUpdateAnswer,
} from '../../../../../../../services/survey.service';
import { ToastSwal } from '../../../../../../components/general/toastSwals/toastSwal';
import FieldText from '../../../../../../components/general/fields/fieldText';
import LoadingButtonGeneral from '../../../../../../components/general/buttons/loadingButtonGeneral';

interface Props {
    open: boolean;
    onClose: (reload?: boolean) => void;
    surveyId: string;
    questionId: string;
    answer?: any;
}

export const ModalRange = ({
    open,
    onClose,
    surveyId,
    questionId,
    answer,
}: Props) => {
    const [loading, setLoading] = useState(false);
    const [otherRange, setOtherRange] = useState(false);

    const formik = useFormik({
        initialValues: {
            range: '1-5',
            range1: '',
            range2: '',
            status: true,
            question: questionId,
            survey: surveyId,
        },
        validationSchema: Yup.object({
            range1: Yup.number().when([], {
                is: () => otherRange,
                then: (schema) =>
                    schema
                        .required('El rango es obligatorio')
                        .typeError('El rango debe ser un número'),
            }),
            range2: Yup.number().when([], {
                is: () => otherRange,
                then: (schema) =>
                    schema
                        .required('El rango es obligatorio')
                        .typeError('El rango debe ser un número'),
            }),
        }),
        onSubmit: async (values) => {
            setLoading(true);
            try {
                const finalRange = otherRange
                    ? `${values.range1}-${values.range2}`
                    : values.range;

                if (answer?.id) {
                    // Para actualizar, no enviamos questionId
                    const payload = {
                        range: finalRange,
                        status: values.status,
                    };
                    await asyncUpdateAnswer(answer.id, payload);
                    ToastSwal.fire({
                        text: 'Rango actualizado exitosamente',
                        icon: 'success',
                        timer: 3000,
                        position: 'center-end',
                    });
                } else {
                    // Para crear, sí enviamos questionId
                    const payload = {
                        questionId: questionId,
                        range: finalRange,
                        status: values.status,
                    };
                    await asyncCreateAnswer(payload);
                    ToastSwal.fire({
                        text: 'Rango creado exitosamente',
                        icon: 'success',
                        timer: 3000,
                        position: 'center-end',
                    });
                }
                onClose(true);
            } catch (error) {
                console.error('Error al guardar el rango:', error);
                ToastSwal.fire({
                    text: 'Error al guardar el rango',
                    icon: 'error',
                    timer: 3000,
                    position: 'center-end',
                });
            } finally {
                setLoading(false);
            }
        },
    });

    useEffect(() => {
        if (answer) {
            const range = answer.range || '1-5';
            if (['1-5', '1-8', '1-10', '1-12'].includes(range)) {
                formik.setFieldValue('range', range);
                setOtherRange(false);
            } else {
                const [rangeStart, rangeEnd] = range.split('-');
                formik.setFieldValue('range1', rangeStart);
                formik.setFieldValue('range2', rangeEnd);
                formik.setFieldValue('range', 'other');
                setOtherRange(true);
            }
            formik.setFieldValue('status', answer.status);
        }
    }, [answer]);

    const handleRangeChange = (e: any) => {
        formik.handleChange(e);
        setOtherRange(e.target.value === 'other');
    };

    return (
        <Dialog open={open} onClose={() => onClose()} maxWidth="sm" fullWidth>
            <DialogTitle>
                <Typography
                    sx={{
                        fontSize: { lg: 27, xs: 23 },
                        mt: { xs: 1.5, sm: 1.5, md: 1.5, lg: 0 },
                        color: 'primary.main',
                    }}
                >
                    {answer ? 'Editar Rango' : 'Nuevo Rango'}
                </Typography>
            </DialogTitle>
            <form onSubmit={formik.handleSubmit}>
                <DialogContent dividers>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <FormControl>
                                <FormLabel>Rango</FormLabel>
                                <RadioGroup
                                    row
                                    name="range"
                                    value={formik.values.range}
                                    onChange={handleRangeChange}
                                >
                                    <FormControlLabel
                                        value="1-5"
                                        control={
                                            <Radio
                                                sx={{
                                                    color: 'primary.main',
                                                    '&.Mui-checked': {
                                                        color: 'primary.main',
                                                    },
                                                }}
                                            />
                                        }
                                        label="1-5"
                                    />
                                    <FormControlLabel
                                        value="1-8"
                                        control={
                                            <Radio
                                                sx={{
                                                    color: 'primary.main',
                                                    '&.Mui-checked': {
                                                        color: 'primary.main',
                                                    },
                                                }}
                                            />
                                        }
                                        label="1-8"
                                    />
                                    <FormControlLabel
                                        value="1-10"
                                        control={
                                            <Radio
                                                sx={{
                                                    color: 'primary.main',
                                                    '&.Mui-checked': {
                                                        color: 'primary.main',
                                                    },
                                                }}
                                            />
                                        }
                                        label="1-10"
                                    />
                                    <FormControlLabel
                                        value="1-12"
                                        control={
                                            <Radio
                                                sx={{
                                                    color: 'primary.main',
                                                    '&.Mui-checked': {
                                                        color: 'primary.main',
                                                    },
                                                }}
                                            />
                                        }
                                        label="1-12"
                                    />
                                    <FormControlLabel
                                        value="other"
                                        control={
                                            <Radio
                                                sx={{
                                                    color: 'primary.main',
                                                    '&.Mui-checked': {
                                                        color: 'primary.main',
                                                    },
                                                }}
                                            />
                                        }
                                        label="Otro"
                                    />
                                </RadioGroup>
                            </FormControl>
                        </Grid>

                        {otherRange && (
                            <>
                                <Grid item xs={6}>
                                    <FieldText
                                        label="Desde (Ej: 1)"
                                        name="range1"
                                        value={formik.values.range1}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        touched={formik.touched.range1}
                                        errors={formik.errors.range1}
                                        required
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <FieldText
                                        label="Hasta (Ej: 6)"
                                        name="range2"
                                        value={formik.values.range2}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        touched={formik.touched.range2}
                                        errors={formik.errors.range2}
                                        required
                                    />
                                </Grid>
                            </>
                        )}
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <LoadingButtonGeneral
                        onClick={() => onClose()}
                        disabled={loading}
                        title="Cancelar"
                        variant="outlined"
                    />
                    <LoadingButtonGeneral
                        onClick={formik.handleSubmit}
                        loading={loading}
                        disabled={loading}
                        title="Guardar"
                    />
                </DialogActions>
            </form>
        </Dialog>
    );
};
